import { getERPClient } from "@/lib/erp/client"
import { useAuthStore } from "@/lib/store/authStore"
import type {
  GBPConnectionStatus,
  GBPLocation,
  GBPCategory,
  CompanyPrefill,
  CreateListingPayload,
  VerificationOptionsResponse,
  VerificationResult,
  PerformanceMetrics,
  SearchKeywordsResponse,
  GBPPeriod,
  GBPReviewsResponse,
  GBPReviewBadge,
  GBPPhotosResponse,
  GBPBusinessInfo,
  GBPDashboardSummary,
} from "./gbpTypes"

const GBP_MODULE = "zaviago_backend.gbp.gbp_api"
const GBP_VERIFY_MODULE = "zaviago_backend.gbp.gbp_verification"
const GBP_INSIGHTS_MODULE = "zaviago_backend.gbp.gbp_insights"
const GBP_REVIEWS_MODULE = "zaviago_backend.gbp.gbp_reviews"
const GBP_PHOTOS_MODULE = "zaviago_backend.gbp.gbp_photos"
const GBP_INFO_MODULE = "zaviago_backend.gbp.gbp_business_info"
const GBP_DASHBOARD_MODULE = "zaviago_backend.gbp.gbp_dashboard"

/** Get company from auth store — matches marketplaceApi.ts pattern */
function getCompany(): string {
  const company = useAuthStore.getState().tenantCompany
  if (!company) throw new Error("No tenant company selected")
  return company
}

export const gbpApi = {
  // ---- Connection (Epic 01) ----

  getConnectionStatus() {
    return getERPClient().call<GBPConnectionStatus>(
      `${GBP_MODULE}.get_connection_status`,
      { company: getCompany() },
    )
  },

  initiateOAuth() {
    return getERPClient().call<{ redirect_url: string }>(
      `${GBP_MODULE}.initiate_oauth`,
      { company: getCompany() },
    )
  },

  listLocations() {
    return getERPClient().call<{ locations: GBPLocation[] }>(
      `${GBP_MODULE}.list_locations`,
      { company: getCompany() },
    )
  },

  selectLocation(locationId: string, accountId: string) {
    return getERPClient().call<{ success: boolean; location_name: string }>(
      `${GBP_MODULE}.select_location`,
      { company: getCompany(), location_id: locationId, account_id: accountId },
    )
  },

  createListing(payload: CreateListingPayload) {
    return getERPClient().call<{
      success: boolean
      location_id: string
      verification_status: string
    }>(`${GBP_MODULE}.create_listing`, { ...payload, company: getCompany() })
  },

  searchCategories(query: string) {
    return getERPClient().call<{ categories: GBPCategory[] }>(
      `${GBP_MODULE}.search_categories`,
      { query },
    )
  },

  getCompanyPrefill() {
    return getERPClient().call<CompanyPrefill>(
      `${GBP_MODULE}.get_company_prefill`,
      { company: getCompany() },
    )
  },

  // ---- Verification (Epic 02) ----

  getVerificationOptions() {
    return getERPClient().call<VerificationOptionsResponse>(
      `${GBP_VERIFY_MODULE}.get_verification_options`,
      { company: getCompany() },
    )
  },

  requestVerification(
    method: string,
    phoneNumber?: string,
    emailAddress?: string,
  ) {
    return getERPClient().call<{ success: boolean; verification_id: string }>(
      `${GBP_VERIFY_MODULE}.request_verification`,
      {
        company: getCompany(),
        method,
        phone_number: phoneNumber,
        email_address: emailAddress,
      },
    )
  },

  completeVerification(pin: string) {
    return getERPClient().call<VerificationResult>(
      `${GBP_VERIFY_MODULE}.complete_verification`,
      { company: getCompany(), pin },
    )
  },

  // ---- Insights (Epic 02) ----

  getPerformanceMetrics(period: GBPPeriod) {
    return getERPClient().call<PerformanceMetrics>(
      `${GBP_INSIGHTS_MODULE}.get_performance_metrics`,
      { company: getCompany(), period },
    )
  },

  getSearchKeywords(period: GBPPeriod) {
    return getERPClient().call<SearchKeywordsResponse>(
      `${GBP_INSIGHTS_MODULE}.get_search_keywords`,
      { company: getCompany(), period },
    )
  },

  // ---- Reviews (Epic 03) ----

  getReviews(pageSize?: number, pageToken?: string) {
    return getERPClient().call<GBPReviewsResponse>(
      `${GBP_REVIEWS_MODULE}.get_reviews`,
      { company: getCompany(), page_size: pageSize, page_token: pageToken },
    )
  },

  replyToReview(reviewId: string, text: string) {
    return getERPClient().call<{ success: boolean }>(
      `${GBP_REVIEWS_MODULE}.reply_to_review`,
      { company: getCompany(), review_id: reviewId, text },
    )
  },

  getReviewBadgeCount() {
    return getERPClient().call<GBPReviewBadge>(
      `${GBP_REVIEWS_MODULE}.get_review_badge_count`,
      { company: getCompany() },
    )
  },

  // ---- Photos (Epic 04) ----

  getPhotos() {
    return getERPClient().call<GBPPhotosResponse>(
      `${GBP_PHOTOS_MODULE}.get_photos`,
      { company: getCompany() },
    )
  },

  async uploadPhoto(file: File) {
    const formData = new FormData()
    formData.append("company", getCompany())
    formData.append("file", file)

    const response = await fetch(
      `/api/method/${GBP_PHOTOS_MODULE}.upload_photo`,
      {
        method: "POST",
        headers: {
          "X-Frappe-CSRF-Token": (window as unknown as Record<string, unknown>).csrf_token as string || "",
        },
        body: formData,
      },
    )

    if (!response.ok) {
      const err = await response.json().catch(() => ({}))
      throw new Error(
        (err as Record<string, string>)._server_messages ||
        (err as Record<string, string>).message ||
        "Upload failed"
      )
    }

    const result = await response.json()
    return result.message as { success: boolean; photo_id: string }
  },

  deletePhoto(photoId: string) {
    return getERPClient().call<{ success: boolean }>(
      `${GBP_PHOTOS_MODULE}.delete_photo`,
      { company: getCompany(), photo_id: photoId },
    )
  },

  // ---- Business Info (Epic 04) ----

  getLocationInfo() {
    return getERPClient().call<GBPBusinessInfo>(
      `${GBP_INFO_MODULE}.get_location_info`,
      { company: getCompany() },
    )
  },

  // ---- Dashboard (Epic 04) ----

  getDashboardSummary() {
    return getERPClient().call<GBPDashboardSummary>(
      `${GBP_DASHBOARD_MODULE}.get_dashboard_summary`,
      { company: getCompany() },
    )
  },

  // ---- Disconnect (Epic 04) ----

  disconnect() {
    return getERPClient().call<{ success: boolean }>(
      `${GBP_MODULE}.disconnect`,
      { company: getCompany() },
    )
  },
}

// OAuth redirect validation
const TRUSTED_GBP_DOMAINS = ["accounts.google.com"]

export function validateGBPRedirectUrl(url: string): boolean {
  try {
    const parsed = new URL(url)
    return TRUSTED_GBP_DOMAINS.some(
      (d) => parsed.hostname === d || parsed.hostname.endsWith(`.${d}`),
    )
  } catch {
    return false
  }
}
